import type { CSSProperties, HTMLAttributes, ReactNode } from 'react'

export type CardSize = 'hero' | 'default' | 'flat'
export type CardTone =
  | 'surface'
  | 'gradient'
  | 'soft'
  | 'primary-soft'
  | 'warning-soft'
  | 'error-soft'
  | 'info-soft'

interface CardProps extends HTMLAttributes<HTMLElement> {
  size?: CardSize
  tone?: CardTone
  className?: string
  style?: CSSProperties
  as?: 'section' | 'div' | 'article' | 'aside'
  children: ReactNode
}

const RADIUS_CLASSES: Record<CardSize, string> = {
  hero: 'rounded-3xl',
  default: 'rounded-2xl',
  flat: 'rounded-2xl',
}

const SIZE_SHADOW_CLASSES: Record<CardSize, string> = {
  hero: 'shadow-brand-lg',
  default: 'shadow-brand',
  flat: '',
}

const TONE_SURFACE_CLASSES: Record<CardTone, string> = {
  surface: 'bg-brand-surface border border-brand-border',
  gradient: 'bg-brand-gradient-hero text-brand-on-primary',
  soft: 'bg-brand-bg-soft border border-brand-border',
  'primary-soft': 'bg-brand-primary-soft border border-brand-border',
  'warning-soft': 'bg-brand-warning-soft border border-brand-warning/30',
  'error-soft': 'bg-brand-error-soft border border-brand-error/30',
  'info-soft': 'bg-brand-info-soft border border-brand-border',
}

const TONE_SHADOW_OVERRIDE: Partial<Record<CardTone, string>> = {
  gradient: 'shadow-brand-hero',
}

export function Card({
  size = 'default',
  tone = 'surface',
  className = '',
  style,
  as: Tag = 'div',
  children,
  ...rest
}: CardProps) {
  const radius = RADIUS_CLASSES[size]
  const shadow = TONE_SHADOW_OVERRIDE[tone] ?? SIZE_SHADOW_CLASSES[size]
  const surface = TONE_SURFACE_CLASSES[tone]
  return (
    <Tag
      className={`${radius} ${shadow} ${surface} ${className}`}
      style={style}
      {...rest}
    >
      {children}
    </Tag>
  )
}
